import * as Api from '@ViewModels';
import moment from 'moment-timezone';

export const nextUpcomingBrief = (
	briefs: Api.IMeetingPrepBrief[],
	now: Date = new Date()
): Api.IMeetingPrepBrief | undefined => {
	let nearest: Api.IMeetingPrepBrief | undefined;
	let nearestTime = Number.POSITIVE_INFINITY;

	briefs.forEach(brief => {
		if (brief.status !== 'Success' || !brief.meetingStartTime) {
			return;
		}
		const startTime = new Date(brief.meetingStartTime).getTime();
		if (startTime > now.getTime() && startTime < nearestTime) {
			nearest = brief;
			nearestTime = startTime;
		}
	});

	return nearest;
};

const coversDay = (day: moment.Moment, weekendDelivery: boolean) =>
	weekendDelivery || (day.isoWeekday() >= 1 && day.isoWeekday() <= 5);

const advanceToCoveredDay = (from: moment.Moment, weekendDelivery: boolean) => {
	const day = from.clone();
	for (let index = 0; index < 7 && !coversDay(day, weekendDelivery); index++) {
		day.add(1, 'day');
	}
	return day;
};

const periodOfDay = (hour: number): string => {
	if (hour < 12) {
		return 'morning';
	}
	return hour < 17 ? 'afternoon' : 'evening';
};

export const nextDeliveryLabel = (
	template: Api.MeetingPrepTemplate,
	timeZone: string,
	now: Date = new Date()
): string => {
	const localNow = moment(now).tz(timeZone);
	const weekendDelivery = !!template.weekendDelivery;
	const isEvening = template.deliveryTiming === Api.MeetingPrepDeliveryTiming.EveningBefore;
	const sendHour = template.sendHourLocal ?? (isEvening ? 19 : 7);

	let coverage = advanceToCoveredDay(localNow.clone().add(1, 'day').startOf('day'), weekendDelivery);
	let delivery = isEvening ? coverage.clone().subtract(1, 'day') : coverage.clone();

	if (delivery.isSame(localNow, 'day') && localNow.hour() >= sendHour) {
		coverage = advanceToCoveredDay(coverage.clone().add(1, 'day'), weekendDelivery);
		delivery = isEvening ? coverage.clone().subtract(1, 'day') : coverage.clone();
	}

	const period = periodOfDay(sendHour);
	if (delivery.isSame(localNow, 'day')) {
		return `this ${period}`;
	}
	if (delivery.isSame(localNow.clone().add(1, 'day'), 'day')) {
		return `tomorrow ${period}`;
	}
	return `${delivery.format('dddd')} ${period}`;
};

export const hasEverDelivered = (template: Api.MeetingPrepTemplate): boolean => !!template.lastRunDate;

// Looks ahead first so evening-before deliveries show tomorrow's briefs that evening; otherwise today.
export const briefingDay = (briefs: Api.IMeetingPrepBrief[], timeZone: string, now: Date = new Date()) => {
	const localNow = moment(now).tz(timeZone);
	const next = nextUpcomingBrief(briefs, now);
	const day = next ? moment(next.meetingStartTime).tz(timeZone) : localNow;
	const startOf = (brief: Api.IMeetingPrepBrief) => moment(brief.meetingStartTime).tz(timeZone);
	const prepped = briefs
		.filter(brief => brief.status === 'Success' && !!brief.meetingStartTime && startOf(brief).isSame(day, 'day'))
		.sort((a, b) => startOf(a).valueOf() - startOf(b).valueOf());
	if (prepped.length === 0) {
		return undefined;
	}

	let name = day.format('dddd');
	if (day.isSame(localNow, 'day')) {
		name = 'today';
	} else if (day.isSame(localNow.clone().add(1, 'day'), 'day')) {
		name = 'tomorrow';
	}
	return { briefs: prepped, name };
};

export const meetingLengthLabel = (brief: Api.IMeetingPrepBrief): string => {
	const minutes =
		brief.durationMinutes ??
		(brief.meetingStartTime && brief.meetingEndTime
			? Math.round((new Date(brief.meetingEndTime).getTime() - new Date(brief.meetingStartTime).getTime()) / 60000)
			: 0);
	if (!minutes || minutes <= 0) {
		return '';
	}
	if (minutes < 60) {
		return `${minutes} min`;
	}
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};

export const briefsInView = (briefs: Api.IMeetingPrepBrief[], limit: number, now: Date = new Date()) => {
	const nextIndex = briefs.findIndex(brief => new Date(brief.meetingStartTime).getTime() > now.getTime());
	const lastStart = Math.max(0, briefs.length - limit);
	const start = nextIndex === -1 ? lastStart : Math.min(nextIndex, lastStart);
	return briefs.slice(start, start + limit);
};
